import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../_db";
import { FootprintProduct } from "@/types/results";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";
import { 
  DistributionTransportData,
  DistributionFormInputs,
  DistributionCalculationInput,
  DistributionCalculationSet,
  DistributionFootprintData,
  DistributionCalculationData
} from "@/types/distribution/distribution.types";

export interface DistributionCalculationInputs {
  transportData: DistributionTransportData[];
  transportDistributions: TransportDistribution[];
  product: FootprintProduct;
  companyType: string;
  footprintType: 'carbon' | 'hydric' | 'both';
}

export interface DistributionCalculationResult {
  carbon_footprint: DistributionFootprintData;
  hydric_footprint: DistributionFootprintData;
  form_inputs: DistributionFormInputs;
}

export class DistributionCalculationService {
  /**
   * Calculate carbon footprint for transport phase
   * Formula: fe_trans × dist × uf × {conditional_factor}
   * Where conditional_factor depends on unit U:
   * - U = m²: (e × densPedra3/densPedra1) × (100/rend) × (1/1000)
   * - U = m³: (densPedra3/densPedra1) × (100/rend) × (1/1000)
   * - U = kg: (1/1000)
   * - U = t: 1
   * 
   * densPedra3 = productWeight (weight of studied product transported)
   * densPedra1 = totalWeight (total weight transported in vehicle)
   */
  private calculateCarbonFootprint(
    transportData: DistributionTransportData,
    transportDistribution: TransportDistribution,
    product: FootprintProduct
  ): DistributionCalculationSet {
    const fe_trans = transportDistribution.fe_trans || transportDistribution.carbon_factor?.value || 0;
    const dist = Number(transportData.distance); // Ensure distance is a number
    const uf = 1; // Utilization factor - assuming 1 for now
    
    // Get product density and functional unit
    const dens_pedra = product.density || 0;
    const functionalUnit = product.functional_unit;
    const unit = functionalUnit.unit;
    
    // Weight ratio: densPedra3/densPedra1 = productWeight/totalWeight
    const productWeight = Number(transportData.productWeight) || 0;
    const totalWeight = Number(transportData.totalWeight) || 1; // Avoid division by zero
    const weightRatio = totalWeight > 0 ? productWeight / totalWeight : 0;
    
    let conditionalFactor: number;
    let formula: string;
    let inputs: DistributionCalculationInput = {
      fe_trans,
      dist,
      uf,
      dens_pedra,
      unit,
      productWeight,
      totalWeight,
      weightRatio
    };

    if (unit === "m2") {
      // For m²: (e × densPedra3/densPedra1) × (100/rend) × (1/1000)
      const e = (functionalUnit.thickness || 2) / 100; // Convert cm to meters, default 2cm
      const rend = product.rendimento || 100;
      conditionalFactor = (e * weightRatio) * (100 / rend) * (1 / 1000);
      formula = "fe_trans × dist × uf × (e × weightRatio × 100/rend × 1/1000)";
      inputs.e = e;
      inputs.rend = rend;
    } else if (unit === "m3") {
      // For m³: (densPedra3/densPedra1) × (100/rend) × (1/1000)
      const rend = product.rendimento || 100; // Use product yield, default 100%
      conditionalFactor = weightRatio * (100 / rend) * (1 / 1000);
      formula = "fe_trans × dist × uf × (weightRatio × 100/rend × 1/1000)";
      inputs.rend = rend;
    } else if (unit === "kg") {
      // For kg: 1 / 1000
      conditionalFactor = 1 / 1000;
      formula = "fe_trans × dist × uf × (1/1000)";
    } else if (unit === "t") {
      // For tonnes: 1
      conditionalFactor = 1;
      formula = "fe_trans × dist × uf × 1";
    } else {
      // Default case - treat as tonnes
      conditionalFactor = 1;
      formula = "fe_trans × dist × uf × 1";
    }

    const result_kgCO2e = fe_trans * dist * uf * conditionalFactor;


    return {
      phase: "transporte",
      formula,
      inputs,
      result_kgCO2e: Number(result_kgCO2e.toFixed(6)) // More precision for small numbers
    };
  }

  /**
   * Calculate hydric footprint for transport phase
   * Formula: fhtrans × dist × uf × {conditional_factor}
   * Where conditional_factor depends on unit U:
   * - U = m²: (e × densPedra3/densPedra1) × (100/rend) × (1/1000)
   * - U = m³: (densPedra3/densPedra1) × (100/rend) × (1/1000)
   * - U = kg: (1/1000)
   * - U = t: 1
   * 
   * densPedra3 = productWeight (weight of studied product transported)
   * densPedra1 = totalWeight (total weight transported in vehicle)
   */
  private calculateHydricFootprint(
    transportData: DistributionTransportData,
    transportDistribution: TransportDistribution,
    product: FootprintProduct
  ): DistributionCalculationSet {
    const fhtrans = transportDistribution.fh_trans || transportDistribution.hydric_factor?.value || 0;
    const dist = Number(transportData.distance); // Ensure distance is a number
    const uf = 1; // Utilization factor - assuming 1 for now
    
    // Get product density and functional unit
    const dens_pedra = product.density || 0;
    const functionalUnit = product.functional_unit;
    const unit = functionalUnit.unit;
    
    // Weight ratio: densPedra3/densPedra1 = productWeight/totalWeight
    const productWeight = Number(transportData.productWeight) || 0;
    const totalWeight = Number(transportData.totalWeight) || 1; // Avoid division by zero
    const weightRatio = totalWeight > 0 ? productWeight / totalWeight : 0;
    
    let conditionalFactor: number;
    let formula: string;
    let inputs: DistributionCalculationInput = {
      fhtrans,
      dist,
      uf,
      dens_pedra,
      unit,
      productWeight,
      totalWeight,
      weightRatio
    };

    if (unit === "m2") {
      // For m²: (e × densPedra3/densPedra1) × (100/rend) × (1/1000)
      const e = (functionalUnit.thickness || 2) / 100; // Convert cm to meters, default 2cm
      const rend = product.rendimento || 100;
      conditionalFactor = (e * weightRatio) * (100 / rend) * (1 / 1000);
      formula = "fhtrans × dist × uf × (e × weightRatio × 100/rend × 1/1000)";
      inputs.e = e;
      inputs.rend = rend;
    } else if (unit === "m3") {
      // For m³: (densPedra3/densPedra1) × (100/rend) × (1/1000)
      const rend = product.rendimento || 100; // Use product yield, default 100%
      conditionalFactor = weightRatio * (100 / rend) * (1 / 1000);
      formula = "fhtrans × dist × uf × (weightRatio × 100/rend × 1/1000)";
      inputs.rend = rend;
    } else if (unit === "kg") {
      // For kg: 1 / 1000
      conditionalFactor = 1 / 1000;
      formula = "fhtrans × dist × uf × (1/1000)";
    } else if (unit === "t") {
      // For tonnes: 1
      conditionalFactor = 1;
      formula = "fhtrans × dist × uf × 1";
    } else {
      // Default case - treat as tonnes
      conditionalFactor = 1;
      formula = "fhtrans × dist × uf × 1";
    }

    const result_m3 = fhtrans * dist * uf * conditionalFactor;


    return {
      phase: "transporte",
      formula,
      inputs,
      result_m3: Number(result_m3.toFixed(9)) // More precision for very small numbers
    };
  }

  /**
   * Calculate distribution footprints for all transport data
   */
  public calculateDistributionFootprints(
    inputs: DistributionCalculationInputs
  ): DistributionCalculationResult {
    const { transportData, transportDistributions, product, companyType, footprintType } = inputs;
    
    const carbonCalculationSets: DistributionCalculationSet[] = [];
    const hydricCalculationSets: DistributionCalculationSet[] = [];

    let totalCarbonFootprint = 0;
    let totalHydricFootprint = 0;

    // Calculate footprints for each transport entry
    transportData.forEach((transportEntry) => {
      const transportDistribution = transportDistributions.find(
        td => td.vehicleType === transportEntry.vehicleType
      );

      if (!transportDistribution) {
        console.warn(`Transport distribution not found for vehicle type: ${transportEntry.vehicleType}`);
        return;
      }

      // Validate transport data
      const distance = Number(transportEntry.distance);
      if (isNaN(distance) || distance <= 0) {
        console.warn(`Invalid distance for transport entry: ${transportEntry.distance}`);
        return;
      }

      // Calculate footprints based on selected type
      if (footprintType === 'carbon' || footprintType === 'both') {
        const carbonResult = this.calculateCarbonFootprint(
          transportEntry,
          transportDistribution,
          product
        );
        carbonCalculationSets.push(carbonResult);
        totalCarbonFootprint += carbonResult.result_kgCO2e || 0;
      }
      
      if (footprintType === 'hydric' || footprintType === 'both') {
        const hydricResult = this.calculateHydricFootprint(
          transportEntry,
          transportDistribution,
          product
        );
        hydricCalculationSets.push(hydricResult);
        totalHydricFootprint += hydricResult.result_m3 || 0;
      }
    });

    const form_inputs: DistributionFormInputs = {
      company_type: companyType,
      distance_km: transportData.reduce((sum, t) => sum + Number(t.distance), 0),
      transport_type: transportData.map(t => t.vehicleType).join(", "),
      material_type: product.stone_type_name || "Unknown",
      material_quantity_ton: transportData.reduce((sum, t) => sum + Number(t.productWeight || 0), 0),
      transport_entries: transportData.length,
      product_name: product.name,
      product_type: product.product_type,
      functional_unit: product.functional_unit
    };

    return {
      carbon_footprint: {
        calculation_sets: carbonCalculationSets,
        total_kgCO2e: Number(totalCarbonFootprint.toFixed(6))
      },
      hydric_footprint: {
        calculation_sets: hydricCalculationSets,
        total_m3: Number(totalHydricFootprint.toFixed(9))
      },
      form_inputs
    };
  }

  /**
   * Update footprint product with distribution calculations
   */
  public async updateFootprintProductWithDistribution(
    productId: string,
    calculationResult: DistributionCalculationResult,
    footprintType?: 'carbon' | 'hydric' | 'both'
  ): Promise<void> {
    try {
      const productRef = doc(db, "footprint_products", productId);
      
      // Get existing product data to access footprint_id for recalculation
      const productSnap = await getDoc(productRef);
      if (!productSnap.exists()) {
        throw new Error("Product not found");
      }
      const productData = productSnap.data() as FootprintProduct;
      
      // Get existing distribution data
      const existingDistribution = await this.getFootprintProductDistribution(productId);
      
      // Merge calculation results with existing data
      const distributionData: Record<string, any> = {
        ...existingDistribution,
      };
      
      if (footprintType === 'carbon' || footprintType === 'both') {
        distributionData.carbon_footprint = calculationResult.carbon_footprint;
      }
      
      if (footprintType === 'hydric' || footprintType === 'both') {
        distributionData.hydric_footprint = calculationResult.hydric_footprint;
      }
      
      distributionData.form_inputs = calculationResult.form_inputs;
      distributionData.calculation_timestamp = new Date().toISOString();

      const updateData = {
        distribution: distributionData as DistributionCalculationData,
        updated_at: new Date()
      };

      await updateDoc(productRef, updateData);

      console.log(`Successfully updated product ${productId} with distribution calculations`);
      
      // Trigger backend recalculation of footprint totals
      await this.triggerFootprintRecalculation(productData.footprint_id);
      
    } catch (error) {
      console.error("Error updating footprint product with distribution:", error);
      throw new Error("Failed to update footprint product with distribution calculations");
    }
  }

  /**
   * Trigger backend recalculation of footprint totals
   * This ensures that the parent footprint document's aggregated totals are updated
   */
  private async triggerFootprintRecalculation(footprintId: string): Promise<void> {
    try {
      const { collection, addDoc } = await import("firebase/firestore");
      
      const requestData = {
        action: "recalculate-footprint-totals",
        data: {
          footprint_id: footprintId,
        },
      };

      const requestRef = await addDoc(collection(db, "requests"), requestData);
      
      console.log(`Triggered footprint recalculation for footprint ${footprintId}, request ID: ${requestRef.id}`);
    } catch (error) {
      console.error("Error triggering footprint recalculation:", error);
    }
  }

  /**
   * Get current distribution data from footprint product
   */
  public async getFootprintProductDistribution(productId: string): Promise<Record<string, any>> {
    try {
      const productRef = doc(db, "footprint_products", productId);
      const productSnap = await getDoc(productRef);
      
      if (productSnap.exists()) {
        const productData = productSnap.data() as FootprintProduct;
        return productData.distribution || {};
      }
      
      return {};
    } catch (error) {
      console.error("Error getting footprint product distribution:", error);
      throw new Error("Failed to get footprint product distribution");
    }
  }
}

export const distributionCalculationService = new DistributionCalculationService();
